import { createV3Queries } from "@query/utils/createV3Queries";
import { sharedFileRequestsApi } from "@api";
import { WSQueryKey } from "@ws-react-query";
import { getRequirementsDependencies } from "../../modules/Requirements/dependencies";
// Cyclic with this module through `dependencies` — safe only because the entry
// is dereferenced inside the lazy `awaitDependencies` callback, never at the top
// of the factory body.
import { queriesRequirements } from "./queriesRequirements";

/**
 * Shared-file-request registry — mirrors `SharedFileRequestsApi` 1:1
 * (`/v3/compliance/shared-file-requests`). Shared-file requests back
 * `DocumentUpload` requirement instances: an upload/submit moves the backing
 * request's status machine and with it the requirement, so writes fan out
 * into the requirement reads.
 *
 * The mutated request is additionally *awaited*, not just invalidated: the
 * requirement configs resolve their status from the cached document
 * (`getSharedFileRequestFromCache`), so a modal that closes before the refetch
 * lands leaves the details screen on the pre-upload status.
 *
 * The requirement instance is awaited for the same reason. Only the progress
 * steps read the shared-file document alone; the details screen's file card,
 * upload/download actions and status pill go through
 * `selectorIsRequirementReadyToUploadFile` / `...ToDownloadFile`, and for a
 * `None`-context request those resolve off `requirement.dataSourceStatus`. The
 * backend moves both documents together, so awaiting only the shared file left
 * the steps showing complete while the rest of the screen stayed on the
 * pre-upload state until a reload.
 *
 * Awaited keys are otherwise EXACT. `awaitDependencies` resolves through
 * `refetchQueries`, which is prefix-matching and includes inactive queries — so
 * awaiting a `queryKeyBase` would sequentially refetch every cached shared-file
 * request before the mutation resolves. `getPayeeRequirement` is the deliberate
 * exception: neither the request nor the response carries the requirement id, so
 * an exact key cannot be built here, and the base key is narrow in practice —
 * only the requirement-details routes read instances individually (tables use
 * `listPayeeRequirements`). The surrounding surfaces stay on `dependencies`,
 * whose invalidation refetches only what is on screen.
 */
const sharedFileVerbConfig = (queries: {
  getSharedFileRequest: {
    getQueryKey: (request: { sharedFileRequestId: string }) => WSQueryKey;
  };
}) => ({
  dependencies: () => getRequirementsDependencies(),
  awaitDependencies: (
    _response: unknown,
    request: { sharedFileRequestId: string }
  ) => [
    queries.getSharedFileRequest.getQueryKey({
      sharedFileRequestId: request.sharedFileRequestId
    }),
    queriesRequirements.getPayeeRequirement.queryKeyBase
  ]
});

export const queriesSharedFileRequests = createV3Queries(
  "sharedFileRequestsApi",
  sharedFileRequestsApi,
  {
    // A create has no pre-existing document to await — only the fan-out.
    createSharedFileRequest: () => ({
      dependencies: () => getRequirementsDependencies()
    }),
    uploadSharedFileRequestFile: sharedFileVerbConfig,
    submitSharedFileRequest: sharedFileVerbConfig
  }
);
